import React from 'react';
import { logError } from '../utils/monitoring';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Código curto de referência pra o usuário passar ao suporte — ajuda a
    // cruzar com o log sem expor a mensagem técnica do erro em produção.
    const errorId = `ERR-${Date.now().toString(36).toUpperCase()}`;

    this.setState({ error, errorInfo, errorId });

    // O componentStack é logado SEMPRE, inclusive em produção — foi o que
    // permitiu localizar o `require()` de asset em Inicio.js (o stack do erro em
    // build de produção só mostra frames internos do react-dom, que é o caminho
    // de LOG do React e não onde o erro nasceu). Só nomes de componente, sem
    // dado de negócio.
    console.group('Error Boundary');
    console.error('Error:', error);
    console.error('Component Stack:', errorInfo.componentStack);
    console.groupEnd();

    logError(error, { componentStack: errorInfo.componentStack, errorId });
  }

  // "Tentar novamente" = recarrega a PÁGINA ATUAL (mesma URL). Como o boundary
  // fica acima do Router e a subárvore que quebrou perde o estado, um reload
  // limpo é a forma mais confiável de voltar a um estado funcional pra o
  // usuário repetir a ação (reabrir a aba/arquivo). Mantém a rota atual — não
  // joga pra outra tela.
  handleRetry = () => {
    window.location.reload();
  };

  // "Voltar ao início" = home REAL da aplicação (/inicio). Antes ia pra "/",
  // que redireciona pro /login — por isso caía na tela de login.
  handleHome = () => {
    window.location.href = '/inicio';
  };

  render() {
    if (this.state.hasError) {
      const isDev = process.env.NODE_ENV === 'development';

      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '24px',
          boxSizing: 'border-box',
          background: 'radial-gradient(1100px 560px at 50% -8%, #123049 0%, #0b1a29 52%, #070f18 100%)',
          fontFamily: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
          color: '#eaf6ff',
        }}>
          <div style={{
            width: '100%',
            maxWidth: '480px',
            background: '#16212f',
            border: '1px solid #27384d',
            borderTop: '1px solid rgba(160,200,240,0.10)',
            borderRadius: '18px',
            padding: '38px 34px 30px',
            boxShadow: '0 18px 50px rgba(0,0,0,0.45), inset 0 1px 0 rgba(160,200,240,0.05)',
            textAlign: 'center',
          }}>
            {/* Ícone: triângulo de alerta em âmbar (sinaliza problema sem o
                vermelho agressivo), dentro de um badge no estilo da app. */}
            <div style={{
              width: '72px',
              height: '72px',
              margin: '0 auto 22px',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, rgba(214,154,78,0.18), rgba(214,154,78,0.05))',
              border: '1px solid rgba(214,154,78,0.32)',
              boxShadow: '0 6px 18px rgba(0,0,0,0.30)',
            }}>
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#e6b168" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>

            <h1 style={{
              fontSize: '22px',
              fontWeight: 700,
              letterSpacing: '-0.01em',
              margin: '0 0 10px',
              color: '#f0f7ff',
            }}>
              Ops! Algo deu errado
            </h1>
            <p style={{ fontSize: '14.5px', lineHeight: 1.6, margin: '0 0 6px', color: '#93a9c0' }}>
              Um erro inesperado interrompeu esta tela. Seus dados estão seguros — nada foi perdido.
            </p>
            <p style={{ fontSize: '13px', lineHeight: 1.6, margin: '0 0 26px', color: '#6f8399' }}>
              Você pode tentar de novo ou voltar para o início.
            </p>

            {isDev && this.state.error && (
              <details style={{
                marginBottom: '24px',
                textAlign: 'left',
                background: '#0d1622',
                border: '1px solid #27384d',
                padding: '14px 16px',
                borderRadius: '10px',
                fontSize: '13px',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                maxHeight: '200px',
                overflow: 'auto',
              }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600, marginBottom: '12px', color: '#e6b168' }}>
                  🐛 Detalhes do erro (Dev Mode)
                </summary>
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, color: '#a9bccf' }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={this.handleRetry}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: '#29b6d8',
                  color: '#04121e',
                  border: '1px solid #29b6d8',
                  padding: '12px 22px',
                  borderRadius: '10px',
                  fontSize: '14.5px',
                  fontWeight: 700,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  transition: 'background 0.16s',
                }}
                onMouseOver={(e) => { e.currentTarget.style.background = '#3cc3e3'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = '#29b6d8'; }}
              >
                ↻ Tentar novamente
              </button>

              <button
                onClick={this.handleHome}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'transparent',
                  color: '#cfe0f0',
                  border: '1px solid #34485f',
                  padding: '12px 22px',
                  borderRadius: '10px',
                  fontSize: '14.5px',
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  transition: 'background 0.16s, border-color 0.16s',
                }}
                onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(160,200,240,0.06)'; e.currentTarget.style.borderColor = '#44607c'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#34485f'; }}
              >
                Voltar ao início
              </button>
            </div>

            {this.state.errorId && (
              <p style={{ marginTop: '22px', fontSize: '12px', color: '#5a6b7e' }}>
                Se o problema continuar, informe ao suporte o código{' '}
                <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#93a9c0' }}>
                  {this.state.errorId}
                </span>
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
